import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, SuccessCircleIcon } from '../components/Icons';
import { StateBadge } from '../components/StateBadge';
import { GlassCard } from '../components/GlassCard';
import { TimelineView } from '../components/TimelineView';
import { EvidenceList } from '../components/EvidenceList';
import { AudioPlayer } from '../components/AudioPlayer';
import { TypingDots } from '../components/TypingDots';
import { Toast } from '../components/Toast';
import { api, ApiError } from '../lib/api';
import { useNotificationStore } from '../stores/useNotificationStore';
import { T } from '../lib/design-tokens';
import { TaskState } from '../types';
import type { TaskRecord, EvidenceRecord } from '../types';

interface TimelineEvent {
  timestamp: string;
  event: string;
  details?: string;
}

interface TaskEvidenceResponse {
  task: TaskRecord;
  evidence: EvidenceRecord[];
  timeline: TimelineEvent[];
}

const TERMINAL_STATES = new Set([
  TaskState.COMPLETED,
  TaskState.FAILED,
  TaskState.CANCELLED,
  TaskState.NO_RESPONSE,
]);

const CANCELLABLE_STATES = new Set([
  TaskState.AWAITING_APPROVAL,
  TaskState.WAITING_RESPONSE,
]);

function timeAgo(ts: string) {
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [task, setTask] = useState<TaskRecord | null>(null);
  const [evidence, setEvidence] = useState<EvidenceRecord[]>([]);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [audioSrc, setAudioSrc] = useState<string | null>(null);
  const [viewingTranscript, setViewingTranscript] = useState<EvidenceRecord | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = useCallback(async () => {
    if (!id) return;
    try {
      const data = await api<TaskEvidenceResponse>(`/tasks/${id}/evidence`);
      setTask(data.task);
      setEvidence(data.evidence);
      setTimeline(data.timeline);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load task');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!task || TERMINAL_STATES.has(task.state)) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    pollRef.current = setInterval(fetchData, 10000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [task?.state, fetchData]);

  const notifications = useNotificationStore((s) => s.notifications);
  const latestNotifId = notifications[0]?.id;
  useEffect(() => {
    if (!id || !latestNotifId) return;
    const latest = notifications[0];
    if (latest && latest.taskId === id && latest.type === 'task_update') {
      fetchData();
    }
  }, [latestNotifId, id, fetchData, notifications]);

  useEffect(() => {
    if (task?.state !== TaskState.APPROVED) {
      setCountdown(null);
      return;
    }
    const execAt = task.approvalTimestamp
      ? new Date(task.approvalTimestamp).getTime() + 10000
      : Date.now() + 10000;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((execAt - Date.now()) / 1000));
      setCountdown(remaining);
      if (remaining <= 0) fetchData();
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [task?.state, task?.approvalTimestamp, fetchData]);

  const handlePlayRecording = async (evidenceId: string) => {
    try {
      const data = await api<{ url: string }>(`/evidence/${evidenceId}/recording`);
      setAudioSrc(data.url);
    } catch {
      setToast('Failed to load recording');
    }
  };

  const handleCancel = async () => {
    if (!id || cancelling) return;
    setCancelling(true);
    try {
      await api(`/task/${id}/cancel`, { method: 'POST' });
      setToast('Task cancelled.');
      fetchData();
    } catch (err) {
      setToast(err instanceof ApiError ? err.message : 'Failed to cancel');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-[760px] mx-auto" style={{ padding: '36px 40px' }}>
        <div style={{ fontFamily: T.sans, fontSize: 14, color: T.cream35 }}>Loading...</div>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="max-w-[760px] mx-auto" style={{ padding: '36px 40px' }}>
        <button
          type="button"
          onClick={() => navigate('/operations')}
          className="flex items-center gap-1.5 border-none bg-transparent cursor-pointer p-0 mb-7"
          style={{ fontSize: 12, color: T.cream35, fontFamily: T.mono, letterSpacing: '0.05em', textTransform: 'uppercase' }}
        >
          <ArrowLeftIcon size={16} />
          Back
        </button>
        <div style={{ fontFamily: T.sans, fontSize: 14, color: T.red }}>{error ?? 'Task not found'}</div>
      </div>
    );
  }

  const progress = timeline.length
    ? Math.round(
        (timeline.filter((e) => e.timestamp && new Date(e.timestamp).getTime() <= Date.now()).length /
          timeline.length) *
          100,
      )
    : 0;

  const canCancel = CANCELLABLE_STATES.has(task.state);
  const isPlanning =
    task.state === TaskState.CLASSIFIED || task.state === TaskState.APPROVED;

  return (
    <div className="max-w-[760px] mx-auto" style={{ padding: '36px 40px' }}>
      <button
        type="button"
        onClick={() => navigate('/operations')}
        className="flex items-center gap-1.5 border-none bg-transparent cursor-pointer p-0 mb-7"
        style={{
          fontSize: 12,
          color: T.cream35,
          fontFamily: T.mono,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = T.cream60)}
        onMouseLeave={(e) => (e.currentTarget.style.color = T.cream35)}
      >
        <ArrowLeftIcon size={16} />
        Back
      </button>

      {/* Header */}
      <div className="mb-9" style={{ animation: 'fadeUp 0.5s ease both' }}>
        <div className="flex items-center gap-3 mb-3">
          <StateBadge state={task.state} size="lg" />
        </div>
        <h1
          style={{
            fontFamily: T.serif,
            fontSize: 28,
            fontWeight: 400,
            color: T.cream,
            lineHeight: 1.3,
            marginBottom: 10,
          }}
        >
          {task.goal}
        </h1>
        <div
          style={{
            display: 'flex',
            gap: 20,
            fontFamily: T.mono,
            fontSize: 11,
            color: T.cream20,
            letterSpacing: '0.03em',
          }}
        >
          {task.company && (
            <span className="flex items-center gap-1.5">
              <span style={{ width: 4, height: 4, borderRadius: '50%', background: T.gold }} />
              {task.company}
            </span>
          )}
          <span style={{ textTransform: 'uppercase' }}>{task.channel}</span>
          <span>{timeAgo(task.createdAt)}</span>
        </div>
      </div>

      {/* Progress bar */}
      {timeline.length > 0 && (
        <div className="mb-9" style={{ animation: 'fadeUp 0.5s ease 0.1s both' }}>
          <div className="flex justify-between mb-2">
            <span style={{ fontFamily: T.mono, fontSize: 10, color: T.cream20, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Progress
            </span>
            <span style={{ fontFamily: T.mono, fontSize: 12, color: T.cream, fontWeight: 700 }}>
              {progress}%
            </span>
          </div>
          <div
            style={{
              height: 4,
              borderRadius: 2,
              background: T.cream04,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                borderRadius: 2,
                background:
                  task.state === TaskState.COMPLETED
                    ? T.green
                    : `linear-gradient(90deg, ${T.gold}, #e8c87c)`,
                width: `${progress}%`,
                transition: 'width 1.2s cubic-bezier(0.4,0,0.2,1)',
              }}
            />
          </div>
        </div>
      )}

      {/* Execution Plan / Timeline */}
      {timeline.length > 0 && (
        <div className="mb-10">
          <h2
            style={{
              fontFamily: T.mono,
              fontSize: 10,
              fontWeight: 600,
              color: T.cream20,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              marginBottom: 20,
            }}
          >
            Execution Plan
          </h2>
          <TimelineView events={timeline} currentState={task.state} />
        </div>
      )}

      {/* Evidence */}
      {evidence.length > 0 && (
        <div className="mb-10" style={{ animation: 'fadeUp 0.5s ease 0.3s both' }}>
          <div className="flex justify-between items-center mb-4">
            <h2
              style={{
                fontFamily: T.mono,
                fontSize: 10,
                fontWeight: 600,
                color: T.cream20,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
              }}
            >
              Evidence
            </h2>
            <button
              type="button"
              className="flex items-center gap-1.5 border rounded-lg py-1.5 px-3.5 cursor-pointer text-[11px] font-semibold"
              style={{
                borderColor: T.cream08,
                color: T.cream35,
                fontFamily: T.mono,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                background: 'transparent',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = T.cream12;
                e.currentTarget.style.color = T.cream60;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = T.cream08;
                e.currentTarget.style.color = T.cream35;
              }}
            >
              Export
            </button>
          </div>
          <EvidenceList
            evidence={evidence}
            onPlayRecording={handlePlayRecording}
            onViewTranscript={(ev) => setViewingTranscript(ev)}
          />
        </div>
      )}

      {audioSrc && (
        <div className="mb-6">
          <AudioPlayer src={audioSrc} onClose={() => setAudioSrc(null)} />
        </div>
      )}

      {viewingTranscript && (
        <GlassCard style={{ padding: '20px 24px', marginBottom: 24 }}>
          <div className="flex justify-between items-center mb-3">
            <h3 style={{ fontFamily: T.sans, fontSize: 14, fontWeight: 600, color: T.cream }}>
              Transcript
            </h3>
            <button
              type="button"
              onClick={() => setViewingTranscript(null)}
              className="border-none bg-transparent cursor-pointer p-0 text-xs"
              style={{ color: T.cream35 }}
            >
              Close
            </button>
          </div>
          <pre
            style={{
              fontFamily: T.sans,
              fontSize: 13,
              lineHeight: 1.7,
              color: T.cream60,
              maxHeight: 300,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
            }}
          >
            {viewingTranscript.transcript}
          </pre>
        </GlassCard>
      )}

      {/* Planning state */}
      {isPlanning && (
        <GlassCard style={{ padding: 32, textAlign: 'center', animation: 'fadeUp 0.5s ease both' }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: `2px solid ${T.cream08}`,
              borderTopColor: T.gold,
              margin: '0 auto 20px',
              animation: 'spin 1s linear infinite',
            }}
          />
          <div style={{ fontFamily: T.sans, fontSize: 16, fontWeight: 500, color: T.cream, marginBottom: 6 }}>
            Building your plan
            <TypingDots />
          </div>
          <div style={{ fontFamily: T.sans, fontSize: 13, color: T.cream35 }}>
            Researching the best approach for {task.company ?? 'this task'}
          </div>
        </GlassCard>
      )}

      {task.state === TaskState.APPROVED && countdown !== null && (
        <GlassCard
          style={{
            padding: '18px 24px',
            marginBottom: 24,
            border: `1px solid ${T.goldDim}`,
            background: T.goldGlow,
          }}
        >
          <div className="flex items-center gap-3">
            <div
              style={{
                fontFamily: T.mono,
                fontSize: 24,
                fontWeight: 700,
                color: T.gold,
                minWidth: 32,
                textAlign: 'center',
              }}
            >
              {countdown}
            </div>
            <div style={{ fontFamily: T.sans, fontSize: 14, color: T.cream60 }}>
              Executing in {countdown} second{countdown !== 1 ? 's' : ''}...
            </div>
          </div>
        </GlassCard>
      )}

      {task.state === TaskState.EXECUTING && !isPlanning && (
        <GlassCard
          style={{
            padding: '18px 24px',
            marginBottom: 24,
            border: `1px solid ${T.goldDim}`,
            background: T.goldGlow,
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="rounded-full border-2 border-t-transparent"
              style={{
                width: 24,
                height: 24,
                borderColor: T.gold,
                borderTopColor: 'transparent',
                animation: 'spin 1s linear infinite',
              }}
            />
            <span style={{ fontFamily: T.sans, fontSize: 14, color: T.cream60 }}>In progress</span>
            <TypingDots />
          </div>
        </GlassCard>
      )}

      {task.state === TaskState.WAITING_RESPONSE && (
        <GlassCard style={{ padding: '18px 24px', marginBottom: 24 }}>
          <div style={{ fontFamily: T.sans, fontSize: 14, color: T.cream60 }}>
            Waiting for response...
          </div>
          {evidence.length > 0 && (
            <div style={{ fontFamily: T.mono, fontSize: 11, color: T.cream35, marginTop: 4 }}>
              Last activity: {timeAgo(evidence[evidence.length - 1]!.timestamp)}
            </div>
          )}
        </GlassCard>
      )}

      {task.state === TaskState.COMPLETED && (
        <div
          style={{
            padding: '24px 28px',
            borderRadius: 14,
            background: T.greenDim,
            border: '1px solid rgba(74,158,110,0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            animation: 'fadeUp 0.5s ease both',
            marginBottom: 24,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'rgba(74,158,110,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: T.green,
            }}
          >
            <SuccessCircleIcon size={20} />
          </div>
          <div>
            <div style={{ fontFamily: T.sans, fontSize: 15, fontWeight: 600, color: T.green }}>
              Operation completed
            </div>
            <div style={{ fontFamily: T.sans, fontSize: 13, color: 'rgba(74,158,110,0.7)' }}>
              All steps finished. Evidence package ready.
            </div>
          </div>
        </div>
      )}

      {task.state === TaskState.FAILED && (
        <GlassCard
          style={{
            padding: '18px 24px',
            marginBottom: 24,
            border: `1px solid ${T.redDim}`,
            background: T.redDim,
          }}
        >
          <div style={{ fontFamily: T.sans, fontSize: 14, fontWeight: 600, color: T.red, marginBottom: 8 }}>
            Operation failed
          </div>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="border-none bg-transparent cursor-pointer p-0 text-xs font-medium"
            style={{ color: T.gold }}
          >
            Try again
          </button>
        </GlassCard>
      )}

      {task.state === TaskState.CANCELLED && (
        <GlassCard style={{ padding: '18px 24px', marginBottom: 24 }}>
          <div style={{ fontFamily: T.sans, fontSize: 14, color: T.cream35 }}>Task cancelled.</div>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="border-none bg-transparent cursor-pointer p-0 mt-2 text-xs font-medium"
            style={{ color: T.gold }}
          >
            Try again
          </button>
        </GlassCard>
      )}

      {task.state === TaskState.NO_RESPONSE && (
        <GlassCard
          style={{
            padding: '18px 24px',
            marginBottom: 24,
            border: `1px solid ${T.goldDim}`,
            background: T.goldGlow,
          }}
        >
          <div style={{ fontFamily: T.sans, fontSize: 14, fontWeight: 600, color: T.gold, marginBottom: 8 }}>
            No response received.
          </div>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="border-none bg-transparent cursor-pointer p-0 text-xs font-medium"
            style={{ color: T.gold }}
          >
            Try again
          </button>
        </GlassCard>
      )}

      {canCancel && (
        <div style={{ paddingTop: 16 }}>
          <button
            type="button"
            onClick={handleCancel}
            disabled={cancelling}
            className="border-none bg-transparent cursor-pointer p-0 text-[11px] font-semibold"
            style={{
              color: T.red,
              fontFamily: T.mono,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              opacity: 0.5,
              transition: 'opacity 0.15s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.5')}
          >
            {cancelling ? 'Cancelling...' : 'Abort operation'}
          </button>
        </div>
      )}

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
